/*
Profile screen — stats + backup ZIP export/import.

Mirrors the Android ProfileScreen at the structural level:
- Stats card: total captures, distinct species, distinct remarquables,
  first capture date.
- Backup card: Export / Import buttons feeding the same ZIP format the
  Android app reads (BackupModels.kt).

Skipped vs. Android (out of skeleton scope):
- Global / Saison vive segmented filter — single-season MVP.
- Badge card — the BadgeEvaluator pipeline is its own feature.
- "Comment jouer" link — no onboarding / WELCOME_REPLAY route yet.
*/

import { navigate, kPaths } from '../router'
import { listCaptures } from '../data/captureRepository'
import { loadPhoto } from '../data/photoStore'
import { ensureSpeciesIndex, speciesNameFor, type SpeciesName } from '../data/speciesIndex'
import type { Capture } from '../data/types'
import { exportBackup, type ExportResult } from '../backup/exporter'
import { importBackup, type ImportResult } from '../backup/importer'
import { ensureArbresFeatures } from '../data/arbresFeatures'
import { ensureArbresDetails } from '../data/arbresDetails'
import { ensureSpeciesInfo } from '../data/speciesInfo'
import { evaluateBadges } from '../data/badgeEvaluator'
import { kBadgeCatalog, kBadgeGlyphs, type BadgeState } from '../data/badge'
import { estimateStorage } from '../data/persistentStorage'
import { ensureDatasetStats } from '../data/datasetStats'
import { renderEmptyState } from './components/EmptyState'
import { renderSkeletonBlock, renderSkeletonCircle } from './components/Skeleton'
import { renderMonthlyChart } from './components/MonthlyChart'

const kFrenchLongDate = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })  // "9 mai 2026" footer date

const kQuotaWarnFraction: number = 0.8  // banner shows when usage / quota crosses this — iOS tends to evict around here
const kMegaByte: number = 1024 * 1024  // formatting helper for the storage banner copy

const kFrenchDate = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })  // first-capture date formatter
const kFrenchDateTime = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })  // timeline row label, "9 mai à 14:32"
const kFrenchTime = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit' })  // "14:32" used in "Aujourd'hui à 14:32"
const kFrenchNumber = new Intl.NumberFormat('fr-FR')  // thousands separator for stats
const kTimelineCap: number = 30  // most-recent captures shown in the timeline section
const kMillisPerDay: number = 24 * 60 * 60 * 1000

interface Stats
{
	totalCaptures: number
	distinctSpecies: number
	remarquablesCount: number
	firstCaptureAt: number | null
}

/*
Mount the profile screen. Renders a static shell synchronously, then
hydrates stats + badge preview async — same pattern as the Arboretum
so back-nav feels instant.
*/
let fActiveObjectUrls: string[] = []  // timeline thumbnail blob URLs created by the current mount, revoked on remount

export function mountProfileScreen(inRoot: HTMLElement): void
{
	revokeObjectUrls()  // clean up timeline thumbnails left by the previous mount

	inRoot.innerHTML = renderShell()
	wireBackNav(inRoot)
	wireBackup(inRoot)
	void hydrateStats(inRoot)
	void hydrateBadgesPreview(inRoot)
	void hydrateStorageWarning(inRoot)
	void hydrateDatasetVersion(inRoot)
	void hydrateTimeline(inRoot)
	void hydrateMonthlyChart(inRoot)
}

function revokeObjectUrls(): void
{
	for (const vUrl of fActiveObjectUrls) URL.revokeObjectURL(vUrl)
	fActiveObjectUrls = []
}

/*
Skeleton placeholders shaped like the real Profile sub-views. Render
shape mirrors the populated layout 1:1 so the page doesn't reflow
when the IDB reads resolve.
*/

function renderProfileStatsSkeleton(): string
{
	return [0, 1, 2].map(() => `
		<div class="profile-stat profile-stat-skeleton">
			${renderSkeletonBlock({ width: '60%', height: '22px' })}
			${renderSkeletonBlock({ width: '80%', height: '11px' })}
		</div>
	`).join('') + `
		<div class="profile-stat profile-stat-wide profile-stat-skeleton">
			${renderSkeletonBlock({ width: '70%', height: '15px' })}
			${renderSkeletonBlock({ width: '40%', height: '11px' })}
		</div>
	`
}

function renderTimelineSkeleton(): string
{
	return [0, 1, 2, 3].map(() => `
		<div class="profile-timeline-row profile-timeline-row-skeleton">
			${renderSkeletonBlock({ width: '48px', height: '48px', radius: '10px' })}
			<div class="profile-timeline-body">
				${renderSkeletonBlock({ width: '55%', height: '14px' })}
				${renderSkeletonBlock({ width: '38%', height: '11px' })}
			</div>
		</div>
	`).join('')
}

function renderBadgesPreviewSkeleton(): string
{
	return [0, 1, 2, 3].map(() => `
		<div class="profile-badge-mini profile-badge-mini-skeleton">
			${renderSkeletonCircle('40px')}
			${renderSkeletonBlock({ width: '70%', height: '10px' })}
		</div>
	`).join('')
}

function renderShell(): string
{
	return `
		<div class="screen profile">
			<header class="arboretum-header">
				<button class="nav-back" id="nav-back" aria-label="Retour">←</button>
				<h1 class="display arboretum-title">Profil</h1>
			</header>

			<div class="profile-body">
				<aside class="profile-quota-banner" id="profile-quota-banner" data-open="false" role="status">
					<span class="profile-quota-banner-icon" aria-hidden="true">📦</span>
					<div class="profile-quota-banner-body">
						<strong class="profile-quota-banner-title">Sauvegarde recommandée</strong>
						<span class="profile-quota-banner-text" id="profile-quota-banner-text"></span>
					</div>
					<button class="profile-quota-banner-cta" type="button" id="profile-quota-banner-cta">Exporter</button>
				</aside>

				<section class="profile-card" id="stats-card">
					<h2 class="profile-section-title">Statistiques</h2>
					<div class="profile-stats" id="stats-grid">
						${renderProfileStatsSkeleton()}
					</div>
				</section>

				<section class="profile-card profile-chart-card" id="chart-card">
					<h2 class="profile-section-title">Captures par mois</h2>
					<div class="profile-chart" id="chart-host">
						${renderSkeletonBlock({ width: '100%', height: '160px', radius: '12px' })}
					</div>
				</section>

				<section class="profile-card profile-timeline-card" id="timeline-card">
					<h2 class="profile-section-title">Activité récente</h2>
					<div class="profile-timeline" id="timeline-list">
						${renderTimelineSkeleton()}
					</div>
				</section>

				<section class="profile-card">
					<h2 class="profile-section-title">Badges</h2>
					<p class="profile-section-blurb" id="badges-blurb">
						${renderSkeletonBlock({ width: '70%', height: '13px' })}
					</p>
					<div class="profile-badges-preview" id="badges-preview">
						${renderBadgesPreviewSkeleton()}
					</div>
					<div class="profile-actions">
						<button class="profile-button profile-button-primary" id="btn-badges">🏅 Voir tous les badges</button>
					</div>
				</section>

				<section class="profile-card">
					<h2 class="profile-section-title">Aide &amp; informations</h2>
					<div class="profile-actions profile-actions-stack">
						<button class="profile-button" id="btn-howto">❓ Comment jouer</button>
						<button class="profile-button" id="btn-about">ℹ️ À propos &amp; attributions</button>
						<button class="profile-button" id="btn-privacy">🔒 Confidentialité</button>
						<button class="profile-button" id="btn-terms">📜 Conditions d'utilisation</button>
					</div>
					<div class="profile-version" aria-label="Version">
						<span class="profile-version-label">Version</span>
						<span class="profile-version-value">${escapeHtml(__APP_BUILD_COUNT__)}</span>
					</div>
				</section>

				<section class="profile-card">
					<h2 class="profile-section-title">Sauvegarde</h2>
					<p class="profile-section-blurb">
						Exporte tes captures dans un ZIP — même format que l'app Android,
						compatible dans les deux sens. Aucun envoi extérieur, le fichier
						reste sur ton appareil.
					</p>
					<div class="profile-actions">
						<button class="profile-button profile-button-primary" id="btn-export">📦 Exporter</button>
						<button class="profile-button" id="btn-import">📥 Importer</button>
					</div>
					<div class="profile-status" id="backup-status" data-variant="info"></div>
					<input type="file" id="import-input" accept=".zip,application/zip,application/octet-stream" hidden />
				</section>

				<footer class="profile-footer" id="profile-footer">
					<span class="profile-footer-line">Données : <a class="profile-footer-link" href="https://opendata.paris.fr/explore/dataset/les-arbres/" target="_blank" rel="noopener">OpenData Paris</a> (ODbL)</span>
					<span class="profile-footer-line" id="profile-footer-dataset">Snapshot…</span>
				</footer>
			</div>
		</div>
	`
}

function wireBackNav(inRoot: HTMLElement): void
{
	inRoot.querySelector('#nav-back')?.addEventListener('click', () => navigate(kPaths.map()))
	inRoot.querySelector('#btn-badges')?.addEventListener('click', () => navigate(kPaths.badges()))
	inRoot.querySelector('#btn-howto')?.addEventListener('click', () => navigate(kPaths.welcome(true)))
	inRoot.querySelector('#btn-about')?.addEventListener('click', () => navigate(kPaths.about()))
	inRoot.querySelector('#btn-privacy')?.addEventListener('click', () => navigate(kPaths.privacy()))
	inRoot.querySelector('#btn-terms')?.addEventListener('click', () => navigate(kPaths.terms()))
}

/*
Wire the export / import buttons. Export runs immediately. Import opens
a file picker, then runs once a file is selected — the picker is the
PWA's analogue of the Android Storage Access Framework's ACTION_OPEN_DOCUMENT.
*/
function wireBackup(inRoot: HTMLElement): void
{
	inRoot.querySelector('#btn-export')?.addEventListener('click', async () => {
		setStatus(inRoot, 'Export en cours…', 'info')
		try
		{
			const vResult = await exportBackup()
			setStatus(inRoot, formatExportSummary(vResult), 'success')
		}
		catch (vErr)
		{
			const vMsg = vErr instanceof Error ? vErr.message : 'Erreur inconnue'
			setStatus(inRoot, `Échec de l'export : ${vMsg}`, 'error')
		}
	})

	const vInput = inRoot.querySelector<HTMLInputElement>('#import-input')
	inRoot.querySelector('#btn-import')?.addEventListener('click', () => {
		if (vInput) { vInput.value = ''; vInput.click() }
	})
	vInput?.addEventListener('change', async () => {
		const vFile = vInput.files?.[0]  // first (and only) selected file
		if (!vFile) return
		setStatus(inRoot, 'Import en cours…', 'info')
		try
		{
			// Pre-warm the trees index so any Arboretum visit after the
			// import finds the species names cached.
			void ensureArbresFeatures().catch(() => { /* not fatal for import */ })

			const vResult = await importBackup(vFile)
			setStatus(inRoot, formatImportSummary(vResult), 'success')
			void hydrateStats(inRoot)  // refresh stats now that captures may have grown
		}
		catch (vErr)
		{
			const vMsg = vErr instanceof Error ? vErr.message : 'Erreur inconnue'
			setStatus(inRoot, `Échec de l'import : ${vMsg}`, 'error')
		}
	})
}

/*
Compute and render stats from IDB. Re-callable after an import to refresh
the counts.
*/
/*
Query the storage estimate and reveal the quota banner when usage
crosses kQuotaWarnFraction. Runs once on mount; the figures are
cheap (a single platform call) but evolve slowly enough that we
don't bother refreshing per-tick.
*/
async function hydrateStorageWarning(inRoot: HTMLElement): Promise<void>
{
	const vBanner = inRoot.querySelector<HTMLElement>('#profile-quota-banner')
	const vText = inRoot.querySelector<HTMLElement>('#profile-quota-banner-text')
	const vCta = inRoot.querySelector<HTMLButtonElement>('#profile-quota-banner-cta')
	if (!vBanner || !vText || !vCta) return

	const vEstimate = await estimateStorage()
	if (!vEstimate || vEstimate.usageFraction < kQuotaWarnFraction)
	{
		vBanner.setAttribute('data-open', 'false')
		return
	}

	const vUsageMb = (vEstimate.usageBytes / kMegaByte).toFixed(0)
	const vQuotaMb = (vEstimate.quotaBytes / kMegaByte).toFixed(0)
	const vPercent = Math.round(vEstimate.usageFraction * 100)
	vText.textContent = `Tu utilises ${vUsageMb} Mo sur ${vQuotaMb} Mo (${vPercent} %). Exporte un ZIP pour ne rien perdre si iOS doit libérer de l'espace.`
	vBanner.setAttribute('data-open', 'true')

	vCta.addEventListener('click', () => {
		// Forward to the existing export button so the actual flow lives
		// in one place. Falling through to the same setStatus path keeps
		// success / error feedback consistent.
		inRoot.querySelector<HTMLElement>('#btn-export')?.click()
	})
}

/*
Surface the dataset snapshot version in the profile footer. Reads the
`generatedAt` ISO 8601 timestamp baked into dataset-stats.json by
tools/build_dataset.py and renders it as "Snapshot OpenData du 9 mai
2026". Falls back to a generic line if the field is absent (older
dataset bundles pre-dating the field).
*/
async function hydrateDatasetVersion(inRoot: HTMLElement): Promise<void>
{
	const vEl = inRoot.querySelector<HTMLElement>('#profile-footer-dataset')
	if (!vEl) return
	try
	{
		const vStats = await ensureDatasetStats()
		if (!vStats.generatedAt)
		{
			vEl.textContent = 'Snapshot OpenData (date inconnue)'
			return
		}
		const vDate = new Date(vStats.generatedAt)  // ISO 8601 with Z suffix → reliable cross-browser parse
		if (Number.isNaN(vDate.getTime()))
		{
			vEl.textContent = 'Snapshot OpenData (date invalide)'
			return
		}
		vEl.textContent = `Snapshot OpenData du ${kFrenchLongDate.format(vDate)}`
	}
	catch
	{
		// Soft degradation: dataset-stats fetch failed (offline, missing
		// asset). Nothing actionable from the user's side, hide the line.
		vEl.textContent = ''
	}
}

/*
Render the activity-feed section: most-recent captures in reverse
chronological order. Each row hands the user off to the species
detail screen with the row's capture id passed via the `highlight`
query param so SpeciesScreen can scroll to + pulse that exact thumb.

Capped at kTimelineCap rows. The `captures` store stays small for a
single-player personal-use app, so we don't paginate; the cap just
keeps DOM cost bounded if someone imports a huge backup.
*/
/*
Render the "Captures par mois" SVG bar chart from listCaptures(). The
card hides itself entirely on the 0-captures path — the empty stats
card already nudges the user toward the map and a second blank panel
is noise.
*/
async function hydrateMonthlyChart(inRoot: HTMLElement): Promise<void>
{
	const vCard = inRoot.querySelector<HTMLElement>('#chart-card')
	const vHost = inRoot.querySelector<HTMLElement>('#chart-host')
	if (!vCard || !vHost) return
	try
	{
		const vCaptures = await listCaptures()
		if (vCaptures.length === 0)
		{
			vCard.style.display = 'none'
			return
		}
		vHost.innerHTML = renderMonthlyChart(vCaptures)
	}
	catch (vErr)
	{
		const vMsg = vErr instanceof Error ? vErr.message : 'Erreur inconnue'
		vHost.innerHTML = `<span class="profile-stats-error">⚠ ${escapeHtml(vMsg)}</span>`
	}
}

async function hydrateTimeline(inRoot: HTMLElement): Promise<void>
{
	const vCard = inRoot.querySelector<HTMLElement>('#timeline-card')
	const vList = inRoot.querySelector<HTMLElement>('#timeline-list')
	if (!vCard || !vList) return

	try
	{
		await ensureSpeciesIndex()
		const vAll = await listCaptures()  // already sorted asc by timestamp
		if (vAll.length === 0)
		{
			// Hide the whole card when there's nothing to show: the
			// 0-captures empty state in the stats card already nudges
			// the user toward the map; a second blank section would be
			// noise.
			vCard.style.display = 'none'
			return
		}

		// Reverse-chronological. We slice() before reverse so we don't
		// mutate the cached array returned by listCaptures.
		const vRecent = vAll.slice().reverse().slice(0, kTimelineCap)
		const vMore = vAll.length - vRecent.length  // count hidden by the cap, surfaced as a footer when > 0

		vList.innerHTML = vRecent.map(renderTimelineRowShell).join('')
			+ (vMore > 0 ? `<div class="profile-timeline-more">+ ${kFrenchNumber.format(vMore)} capture${vMore > 1 ? 's' : ''} plus anciennes</div>` : '')

		// Wire row clicks (navigation) before async thumb hydration so
		// taps fire even while the photos are still streaming in.
		vList.querySelectorAll<HTMLElement>('.profile-timeline-row').forEach((inRow) => {
			inRow.addEventListener('click', () => {
				const vSk = Number(inRow.dataset.sk)
				const vCaptureIdRaw = inRow.dataset.captureId
				const vCaptureId = vCaptureIdRaw && vCaptureIdRaw.length > 0 ? Number(vCaptureIdRaw) : null
				if (!Number.isFinite(vSk)) return
				navigate(kPaths.species(vSk, false, vCaptureId))
			})
		})

		void hydrateTimelineThumbs(vList, vRecent)
	}
	catch (vErr)
	{
		const vMsg = vErr instanceof Error ? vErr.message : 'Erreur inconnue'
		vList.innerHTML = `<span class="profile-stats-error">⚠ ${escapeHtml(vMsg)}</span>`
	}
}

/*
Per-row markup. Thumbnail slot is empty at this stage; the async
thumb hydration step injects the <img> once its blob loads. We
include the species name + relative date inline so the row reads
even without the thumbnail (useful before the IDB blob lands).
*/
function renderTimelineRowShell(inCap: Capture): string
{
	const vName = speciesNameFor(inCap.speciesIndex)
	const vTitle = vName?.commonName ?? formatLatin(vName)
	const vSubtitle = vName?.commonName ? formatLatin(vName) : ''
	const vDate = renderRelativeDate(inCap.timestamp)
	const vCaptureIdAttr = typeof inCap.id === 'number' ? `data-capture-id="${inCap.id}"` : ''
	return `
		<button class="profile-timeline-row" type="button" data-sk="${inCap.speciesIndex}" ${vCaptureIdAttr}>
			<span class="profile-timeline-thumb" data-photo="${escapeAttr(inCap.photoFilename ?? '')}" aria-hidden="true">
				<span class="profile-timeline-thumb-fallback">🌳</span>
			</span>
			<span class="profile-timeline-body">
				<span class="profile-timeline-title">${escapeHtml(vTitle)}</span>
				${vSubtitle ? `<span class="profile-timeline-subtitle"><em>${escapeHtml(vSubtitle)}</em></span>` : ''}
				<span class="profile-timeline-date">${escapeHtml(vDate)}</span>
			</span>
			<span class="profile-timeline-chevron" aria-hidden="true">›</span>
		</button>
	`
}

/*
Async-load the thumbnail Blob for each row. The IDB read is cheap
(`get` by primary key), but doing them serially keeps the iOS
disk-IO pressure low under WebKit's tight quota.
*/
async function hydrateTimelineThumbs(inList: HTMLElement, inCaptures: Capture[]): Promise<void>
{
	for (const vCap of inCaptures)
	{
		if (!vCap.photoFilename) continue
		const vSlot = inList.querySelector<HTMLElement>(
			`.profile-timeline-thumb[data-photo="${cssEscapeAttr(vCap.photoFilename)}"]`,
		)
		if (!vSlot) continue
		try
		{
			const vBlob = await loadPhoto(vCap.photoFilename)
			if (!vBlob) continue
			const vUrl = URL.createObjectURL(vBlob)
			fActiveObjectUrls.push(vUrl)
			const vImg = document.createElement('img')
			vImg.alt = ''
			vImg.loading = 'lazy'
			vImg.src = vUrl
			// Replace the placeholder fallback glyph with the real img.
			vSlot.replaceChildren(vImg)
		}
		catch
		{
			// Soft fail — keep the fallback glyph; the row's text is
			// still informative and the click still navigates.
		}
	}
}

/*
Format the capture timestamp relative to `now` for the timeline row.
- Today  → "Aujourd'hui à 14:32"
- Yesterday → "Hier à 14:32"
- Past 7 days → "Il y a N jours"
- Older → "9 mai · 14:32"
Keeps text short enough to fit on one line on phone widths.
*/
function renderRelativeDate(inTimestamp: number): string
{
	const vNow = Date.now()
	const vDate = new Date(inTimestamp)
	const vToday = startOfDay(new Date(vNow))
	const vRowDay = startOfDay(vDate)
	const vDayDelta = Math.round((vToday.getTime() - vRowDay.getTime()) / kMillisPerDay)
	if (vDayDelta <= 0) return `Aujourd'hui à ${kFrenchTime.format(vDate)}`
	if (vDayDelta === 1) return `Hier à ${kFrenchTime.format(vDate)}`
	if (vDayDelta < 7) return `Il y a ${vDayDelta} jours`
	return kFrenchDateTime.format(vDate)
}

function startOfDay(inDate: Date): Date
{
	const vCopy = new Date(inDate.getTime())
	vCopy.setHours(0, 0, 0, 0)
	return vCopy
}

/*
Escape a value before embedding it in a CSS attribute selector. We
own the input shape (UUID + ".jpg") so the brace-stripping below is
defensive belt-and-suspenders against future refactors that broaden
the filename grammar.
*/
function cssEscapeAttr(inValue: string): string
{
	if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(inValue)
	return inValue.replace(/["\\]/g, (vCh) => `\\${vCh}`)  // minimum viable for filenames pre-CSS.escape
}

function escapeAttr(inText: string): string { return escapeHtml(inText) }

function formatLatin(inName: SpeciesName | null): string
{
	if (!inName) return 'Espèce inconnue'
	const vEspece = inName.espece.length > 0 ? ` ${inName.espece}` : ''
	return `${inName.genre}${vEspece}`
}

async function hydrateStats(inRoot: HTMLElement): Promise<void>
{
	const vGrid = inRoot.querySelector<HTMLElement>('#stats-grid')
	if (!vGrid) return
	try
	{
		const vStats = await loadStats()
		// 0-captures empty state — replace the zero-stats grid with an
		// invitational illustration + map CTA so the screen tells the
		// user what to do next instead of just reading "0 captures /
		// 0 espèces / 0 remarquables / —". The container is normally a
		// 3-column grid; we override to block so the empty state spans
		// the full card width, and reset to '' (= use the stylesheet
		// default) when re-rendering the populated stats.
		if (vStats.totalCaptures === 0)
		{
			vGrid.style.display = 'block'
			vGrid.innerHTML = renderEmptyState({
				variant: 'profile',
				title: 'Ton arboretum est encore vide',
				body: 'Pars repérer un arbre depuis la carte, approche-toi à moins de 30 m, et capture-le. Ton profil se remplit au fil de tes promenades.',
				ctaLabel: '↗ Aller sur la carte',
				ctaHref: kPaths.map(),
			})
			return
		}
		vGrid.style.display = ''
		vGrid.innerHTML = renderStats(vStats)
	}
	catch (vErr)
	{
		const vMsg = vErr instanceof Error ? vErr.message : 'Erreur inconnue'
		vGrid.innerHTML = `<span class="profile-stats-error">⚠ ${escapeHtml(vMsg)}</span>`
	}
}

async function loadStats(): Promise<Stats>
{
	const vCaptures = await listCaptures()  // chronological
	const vSpecies = new Set<number>()  // distinct speciesIndex values
	const vRemarquables = new Set<number>()  // distinct arbreIds for remarquable trees, populated below
	let vFirst: number | null = null  // earliest timestamp, null when no captures
	for (const vCap of vCaptures)
	{
		vSpecies.add(vCap.speciesIndex)
		if (vFirst === null || vCap.timestamp < vFirst) vFirst = vCap.timestamp
	}

	// Remarquable count requires the arbres index — best-effort: if the
	// 32 MB geojson hasn't been parsed yet we report 0 rather than
	// blocking the stats render. The Arboretum / capture flow will
	// eventually warm the cache.
	try
	{
		const vFeatures = await ensureArbresFeatures()
		const vRemarquableIds = new Set<number>(vFeatures.filter((inF) => inF.remarquable).map((inF) => inF.id))
		for (const vCap of vCaptures)
		{
			if (vRemarquableIds.has(vCap.arbreId)) vRemarquables.add(vCap.arbreId)
		}
	}
	catch
	{
		// Soft degradation — leave vRemarquables empty.
	}

	return {
		totalCaptures: vCaptures.length,
		distinctSpecies: vSpecies.size,
		remarquablesCount: vRemarquables.size,
		firstCaptureAt: vFirst,
	}
}

function renderStats(inStats: Stats): string
{
	const vFirst = inStats.firstCaptureAt !== null
		? kFrenchDate.format(new Date(inStats.firstCaptureAt))
		: '—'  // em-dash placeholder when nothing captured yet

	return `
		<div class="profile-stat">
			<span class="profile-stat-num">${kFrenchNumber.format(inStats.totalCaptures)}</span>
			<span class="profile-stat-label">captures</span>
		</div>
		<div class="profile-stat">
			<span class="profile-stat-num">${kFrenchNumber.format(inStats.distinctSpecies)}</span>
			<span class="profile-stat-label">espèces</span>
		</div>
		<div class="profile-stat">
			<span class="profile-stat-num">${kFrenchNumber.format(inStats.remarquablesCount)}</span>
			<span class="profile-stat-label">remarquables</span>
		</div>
		<div class="profile-stat profile-stat-wide">
			<span class="profile-stat-num profile-stat-num-text">${escapeHtml(vFirst)}</span>
			<span class="profile-stat-label">1re capture</span>
		</div>
	`
}

function formatExportSummary(inResult: ExportResult): string
{
	const vMb = (inResult.bytes / (1024 * 1024)).toFixed(1)
	const vMissing = inResult.photosMissing > 0 ? `, ${inResult.photosMissing} photo(s) manquante(s)` : ''
	return `✓ Backup créé · ${inResult.captureCount} capture(s), ${inResult.photoCount} photo(s) (${vMb} Mo)${vMissing}.`
}

function formatImportSummary(inResult: ImportResult): string
{
	const vMissing = inResult.photosMissing > 0 ? `, ${inResult.photosMissing} photo(s) manquante(s)` : ''
	return `✓ Import OK · ${inResult.importedCount} ajoutée(s), ${inResult.skippedDuplicates} doublon(s)${vMissing}.`
}

function setStatus(inRoot: HTMLElement, inText: string, inVariant: 'info' | 'success' | 'error'): void
{
	const vEl = inRoot.querySelector<HTMLElement>('#backup-status')
	if (!vEl) return
	vEl.textContent = inText
	vEl.dataset.variant = inVariant
}

function escapeHtml(inText: string): string
{
	return inText
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')
}

const kPreviewSlots: number = 4  // miniature badges shown before "Voir tous les badges"

/*
Render a tiny strip of 4 badges above the "Voir tous les badges" CTA.
Selection: most-recently unlocked first, padded with the next locked
badges in catalogue order. Always shows kPreviewSlots cards so the
layout stays stable regardless of progress.
*/
async function hydrateBadgesPreview(inRoot: HTMLElement): Promise<void>
{
	const vBlurb = inRoot.querySelector<HTMLElement>('#badges-blurb')
	const vPreview = inRoot.querySelector<HTMLElement>('#badges-preview')
	if (!vPreview) return

	try
	{
		const [vCaptures, vFeatures, vInfo, vDetails] = await Promise.all([
			listCaptures(),
			ensureArbresFeatures(),
			ensureSpeciesInfo(),
			ensureArbresDetails(),
		])
		const vById = new Map<number, typeof vFeatures[number]>()  // arbreId -> feature, only for ones the user touched
		for (const vCap of vCaptures)
		{
			const vF = vFeatures.find((inF) => inF.id === vCap.arbreId)
			if (vF) vById.set(vCap.arbreId, vF)
		}
		const vStates = evaluateBadges({ captures: vCaptures, arbresById: vById, detailsById: vDetails, speciesInfo: vInfo })
		const vUnlocked = vStates.filter((inS) => inS.unlockedAt !== null).length

		if (vBlurb)
		{
			vBlurb.textContent = `${vUnlocked} / ${kBadgeCatalog.length} débloqués · 6 catégories.`
		}

		const vSelection = pickPreviewBadges(vStates)
		vPreview.innerHTML = vSelection.map(renderMiniBadge).join('')
	}
	catch (vErr)
	{
		const vMsg = vErr instanceof Error ? vErr.message : 'Erreur inconnue'
		console.warn('Badge preview failed', vErr)
		if (vBlurb) vBlurb.textContent = `⚠ ${vMsg}`
		vPreview.innerHTML = ''
	}
}

/*
Pick the kPreviewSlots badges to show. Priority:
  1. Unlocked badges, most recent first (so the user's latest wins
     are visible at a glance).
  2. Pad with the next locked-but-implemented badges in catalogue
     order — these are the obvious "next goals".
  3. If still short, fall through to locked-and-unimplemented (the
     four data-gap entries) just to keep the strip filled.
*/
function pickPreviewBadges(inStates: BadgeState[]): BadgeState[]
{
	const vUnlocked = inStates
		.filter((inS) => inS.unlockedAt !== null)
		.sort((inA, inB) => (inB.unlockedAt as number) - (inA.unlockedAt as number))
	const vLockedImpl = inStates.filter((inS) => inS.unlockedAt === null && inS.def.implemented)
	const vLockedDataGap = inStates.filter((inS) => inS.unlockedAt === null && !inS.def.implemented)

	const vOut: BadgeState[] = []  // result list, capped at kPreviewSlots
	for (const vList of [vUnlocked, vLockedImpl, vLockedDataGap])
	{
		for (const vS of vList)
		{
			if (vOut.length >= kPreviewSlots) break
			vOut.push(vS)
		}
		if (vOut.length >= kPreviewSlots) break
	}
	return vOut
}

function renderMiniBadge(inState: BadgeState): string
{
	const vUnlocked = inState.unlockedAt !== null
	const vGlyph = kBadgeGlyphs[inState.def.category]
	const vBlocked = !inState.def.implemented
	return `
		<div class="profile-badge-mini" data-unlocked="${vUnlocked ? 'true' : 'false'}" data-blocked="${vBlocked ? 'true' : 'false'}" title="${escapeHtml(inState.def.label)}">
			<div class="profile-badge-mini-glyph" aria-hidden="true">${vGlyph}</div>
			<div class="profile-badge-mini-label">${escapeHtml(inState.def.label)}</div>
		</div>
	`
}
